import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  BadgeCheck,
  CalendarDays,
  GraduationCap,
  Mail,
  MapPin,
  Search,
  User,
} from 'lucide-react';
import api from '../../services/api';

const DetailRow = ({ label, value, mono = false }) => (
  <div className="flex items-start justify-between gap-4 border-b border-ink-100 py-3 last:border-0">
    <dt className="flex-shrink-0 text-sm text-ink-500">{label}</dt>
    <dd
      className={`min-w-0 break-words text-right text-sm font-medium text-ink-900 ${
        mono ? 'font-mono' : ''
      }`}
    >
      {value || '—'}
    </dd>
  </div>
);

/**
 * Public graduate lookup: search form, verification result card and support note.
 * Rendered by src/pages/SearchPage.jsx.
 */
const GraduateSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [searchType, setSearchType] = useState('admin');
  const [searchValue, setSearchValue] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [activeGraduationDay, setActiveGraduationDay] = useState(null);

  useEffect(() => {
    fetchActiveGraduationDay();
  }, []);

  // Support shareable result links such as /search?q=DEE/001/S2021
  useEffect(() => {
    if (initialQuery) {
      runSearch(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchActiveGraduationDay = async () => {
    try {
      const response = await api.get('/graduation-days/active');
      setActiveGraduationDay(response.data);
    } catch (error) {
      console.error('Error fetching active graduation day:', error);
    }
  };

  const runSearch = async (value) => {
    const query = value.trim();
    if (!query) {
      setError('Please enter a search value');
      return;
    }

    if (query !== searchParams.get('q')) {
      setSearchParams({ q: query }, { replace: true });
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await api.get(`/graduates/search?query=${encodeURIComponent(query)}`);
      if (response.data) {
        setResults(response.data);
      } else {
        setError('No graduate found with the provided information');
      }
    } catch (error) {
      console.error('Search error:', error);
      if (error.response?.status === 404) {
        setError('No graduate found with the provided information');
      } else if (error.response?.status === 400) {
        setError('Please enter a valid search value');
      } else {
        setError('An error occurred while searching. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    await runSearch(searchValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
  };

  const getGraduationStatus = () => {
    if (!results) return { text: '—', class: 'text-ink-600' };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let ceremonyDate = null;

    if (results.ceremony_date) {
      ceremonyDate = new Date(results.ceremony_date);
      ceremonyDate.setHours(0, 0, 0, 0);
    } else if (activeGraduationDay && activeGraduationDay.ceremony_date) {
      ceremonyDate = new Date(activeGraduationDay.ceremony_date);
      ceremonyDate.setHours(0, 0, 0, 0);
    }

    if (ceremonyDate) {
      if (today < ceremonyDate) return { text: 'Graduating', class: 'text-brand-700' };
      return { text: 'Graduated', class: 'text-emerald-700' };
    }

    if (results.graduation_year) {
      const gradYear = parseInt(results.graduation_year);
      const currentYear = today.getFullYear();

      if (gradYear > currentYear) return { text: 'Graduating', class: 'text-brand-700' };
      if (gradYear < currentYear) return { text: 'Graduated', class: 'text-emerald-700' };

      const currentMonth = today.getMonth();
      if (currentMonth >= 5) return { text: 'Graduated', class: 'text-emerald-700' };
      return { text: 'Graduating', class: 'text-brand-700' };
    }

    return { text: 'Graduated', class: 'text-emerald-700' };
  };

  const getCourseDisplay = () => {
    if (!results) return '—';

    let courseText = results.course_name || '';

    if (results.level_number) {
      courseText += ` (Level ${results.level_number})`;
    } else if (results.level_name) {
      courseText += ` (${results.level_name})`;
    }

    return courseText;
  };

  const getGradeBadgeClass = (grade) => {
    switch (grade) {
      case 'Distinction':
        return 'bg-gold-100 text-gold-800';
      case 'Credit':
        return 'bg-brand-100 text-brand-800';
      case 'Pass':
        return 'bg-emerald-100 text-emerald-800';
      case 'Mastery':
        return 'bg-violet-100 text-violet-800';
      case 'Proficiency':
        return 'bg-indigo-100 text-indigo-800';
      case 'Competent':
        return 'bg-cyan-100 text-cyan-800';
      case 'First Class':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-ink-100 text-ink-700';
    }
  };

  const status = getGraduationStatus();
  const ceremonyDate = results?.ceremony_date || activeGraduationDay?.ceremony_date || null;

  return (
    <div className="container-page py-8 sm:py-10">
      <div className="mx-auto max-w-3xl">
        <div className="card mb-6">
          <form onSubmit={handleSearch}>
            <fieldset className="mb-4">
              <legend className="label">Search by</legend>
              <div
                className="inline-flex rounded-lg bg-ink-100 p-1"
                role="radiogroup"
                aria-label="Search by"
              >
                {[
                  { value: 'admin', label: 'Admission number' },
                  { value: 'telephone', label: 'Telephone number' },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`cursor-pointer rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                      searchType === option.value
                        ? 'bg-white text-brand-700 shadow-sm'
                        : 'text-ink-500 hover:text-ink-700'
                    }`}
                  >
                    <input
                      type="radio"
                      name="searchType"
                      value={option.value}
                      checked={searchType === option.value}
                      onChange={(e) => setSearchType(e.target.value)}
                      className="sr-only"
                    />
                    {option.label}
                  </label>
                ))}
              </div>
              <p className="mt-2 text-xs text-ink-500">
                {searchType === 'admin'
                  ? 'Format: DEE/001/S2021 or any valid admission number'
                  : 'Format: 07XXXXXXXX or 2547XXXXXXXX'}
              </p>
            </fieldset>

            <div className="relative">
              <label htmlFor="search-value" className="sr-only">
                {searchType === 'admin' ? 'Admission number' : 'Telephone number'}
              </label>
              <input
                id="search-value"
                type="text"
                inputMode={searchType === 'telephone' ? 'tel' : 'text'}
                placeholder={searchType === 'admin' ? 'e.g. DEE/001/S2021' : 'e.g. 0712345678'}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="input-field h-14 pr-36 text-base"
                required
              />
              <button
                type="submit"
                disabled={loading}
                className="btn-primary absolute inset-y-1.5 right-1.5 min-h-0 px-4 text-sm"
              >
                {loading ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    Searching
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" aria-hidden="true" />
                    Search
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {results && (
          <div className="overflow-hidden rounded-xl border border-ink-200 bg-white shadow-card">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-ink-200 bg-brand-50/70 px-5 py-5 sm:px-6">
              <div className="min-w-0">
                <p className="text-2xs font-bold uppercase tracking-widest text-brand-600">
                  Graduate details
                </p>
                <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-ink-900">
                  <User className="h-5 w-5 flex-shrink-0 text-brand-600" aria-hidden="true" />
                  <span className="min-w-0 break-words">{results.name}</span>
                </h2>
                <p className="mt-1 font-mono text-xs text-ink-500">{results.admin_number}</p>
              </div>

              <div className="flex flex-col items-start gap-2 sm:items-end">
                <span className="badge-success">
                  <BadgeCheck className="h-3 w-3" aria-hidden="true" />
                  Verified
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 text-sm font-semibold ${status.class}`}
                >
                  {status.text === 'Graduating' ? (
                    <GraduationCap className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <BadgeCheck className="h-4 w-4" aria-hidden="true" />
                  )}
                  {status.text}
                </span>
              </div>
            </div>

            <dl className="grid gap-x-10 px-5 py-2 sm:grid-cols-2 sm:px-6">
              <DetailRow label="Telephone" value={results.telephone} />
              <DetailRow label="Email" value={results.email} />
              <DetailRow label="Department" value={results.department_name} />
              <DetailRow label="Course" value={getCourseDisplay()} />
              <DetailRow label="Exam body" value={results.exam_body_name} />
              <DetailRow label="Graduation year" value={results.graduation_year} />
              <DetailRow label="Graduation date" value={formatDate(ceremonyDate)} />
              <div className="flex items-center justify-between gap-4 border-b border-ink-100 py-3 sm:border-0">
                <dt className="text-sm text-ink-500">Grade</dt>
                <dd>
                  <span className={`badge ${getGradeBadgeClass(results.grade)}`}>
                    {results.grade || 'N/A'}
                  </span>
                </dd>
              </div>
            </dl>

            {status.text === 'Graduating' && ceremonyDate && (
              <div className="border-t border-ink-200 bg-gold-50 px-5 py-4 sm:px-6">
                <p className="flex flex-wrap items-center gap-2 text-sm text-gold-900">
                  <CalendarDays className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  <span>
                    Your ceremony is scheduled for{' '}
                    <strong className="font-semibold">{formatDate(ceremonyDate)}</strong>
                    {activeGraduationDay?.venue && (
                      <>
                        {' '}
                        at{' '}
                        <span className="inline-flex items-center gap-1 font-semibold">
                          <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                          {activeGraduationDay.venue}
                        </span>
                      </>
                    )}
                  </span>
                </p>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-ink-200 bg-ink-50 px-5 py-3 sm:px-6">
              <p className="text-xs text-ink-500">Official record from Katine TTI</p>
              <p className="text-xs text-ink-400">
                Printed {new Date().toLocaleDateString('en-KE')}
              </p>
            </div>
          </div>
        )}

        <div className="mt-6 rounded-xl border border-ink-200 bg-white p-4 text-center">
          <p className="text-xs text-ink-500">
            Need help? Contact the registrar&apos;s office at{' '}
            <a
              href="mailto:registrar@katinetti.ac.ke"
              className="inline-flex items-center gap-1 font-semibold text-brand-700 hover:underline"
            >
              <Mail className="h-3.5 w-3.5" aria-hidden="true" />
              registrar@katinetti.ac.ke
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default GraduateSearch;
